import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@risksmart-app/atomic-ui';
import type { ZodType } from 'zod';

import { type FieldEditorValues, type SectionEditorValues } from './config';
import { DeleteImpactConfirmationDialog } from './parts/DeleteImpactConfirmationDialog';
import { ExclusiveSwitchGroup } from './parts/ExclusiveSwitchGroup';
import { FieldEditorConfigFields } from './parts/FieldEditorConfigFields';
import { FormEditorMainDialog } from './parts/FormEditorMainDialog';
import { FormTextField } from './parts/FormFields';
import { SubEditorDialog } from './parts/SubEditorDialog';
import { buildRequiredFieldSectionLockReason } from './requiredFieldGuard';
import type { FormEditorDialogViewModel } from './useFormEditorDialog';

export const FormEditorDialogView = ({
  activeView,
  isDirty,
  handleClose,
  handleSave,
  formEditorLang,
  containerOrder,
  containers,
  fields,
  sectionNames,
  fieldConfigs,
  sectionConfigs,
  disallowedDrop,
  handleMove,
  canMove,
  handleDragEnd,
  handleDragCancel,
  setContainerOrder,
  setEditingFieldId,
  navigateToSubEditor,
  handleDeleteField,
  setEditingSectionId,
  handleDeleteSection,
  setAddingFieldToSectionId,
  returnToFormEditor,
  sectionBuilderLang,
  sectionSchema,
  sectionBuilderDefaults,
  handleSectionSubmit,
  sectionFieldOptions,
  resolvedGetValueOptions,
  editingSectionId,
  sectionDisallowedCycleCandidates,
  sectionProtectedFieldIds,
  fieldEditorLang,
  editingFieldId,
  fieldSchema,
  fieldEditorDefaults,
  handleFieldSubmit,
  canEditOptions,
  isFieldTypeDisabled,
  canEditLabel,
  isStandardField,
  editingFieldIsRuleSource,
  editingFieldIsPersistedCustom,
  editingFieldOriginalLabel,
  isAltValuesEnabled,
  fieldOptions,
  fieldDisallowedCycleCandidates,
  editingFieldAllowsTarget,
  editingFieldConditionLocked,
  editingFieldHasLockedRules,
  pendingDeletion,
  setPendingDeletion,
  handleConfirmDeletion,
  open,
  saving,
  renderPreview,
  fieldTypeOptions,
}: FormEditorDialogViewModel) => {
  return (
    <>
      <FormEditorMainDialog
        open={open && activeView === 'form-editor'}
        isDirty={isDirty}
        onClose={handleClose}
        onSave={handleSave}
        saving={saving}
        lang={formEditorLang}
        containerOrder={containerOrder}
        containers={containers}
        fields={fields}
        sectionNames={sectionNames}
        fieldConfigs={fieldConfigs}
        sectionConfigs={sectionConfigs}
        renderPreview={renderPreview}
        fieldTypeOptions={fieldTypeOptions}
        disallowedDrop={disallowedDrop}
        onMove={handleMove}
        canMove={canMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onReorderContainers={(ids) => setContainerOrder(ids.map(String))}
        onEditField={(fieldId) => {
          setEditingFieldId(fieldId);
          navigateToSubEditor('field-editor', fieldId);
        }}
        onDeleteField={handleDeleteField}
        onEditSection={(sectionId) => {
          setEditingSectionId(sectionId);
          navigateToSubEditor('section-builder', sectionId);
        }}
        onDeleteSection={handleDeleteSection}
        onAddField={(sectionId) => {
          setEditingFieldId(null);
          setAddingFieldToSectionId(sectionId);
          navigateToSubEditor('field-editor', sectionId);
        }}
        onAddSection={() => {
          setEditingSectionId(null);
          // Nearest anchor on cancel; a successful submit overwrites it.
          navigateToSubEditor(
            'section-builder',
            containerOrder[containerOrder.length - 1]
          );
        }}
      />
      <SubEditorDialog
        open={open && activeView === 'section-builder'}
        onCancel={returnToFormEditor}
        title={sectionBuilderLang.title}
        saveLabel={
          editingSectionId
            ? sectionBuilderLang.editSave
            : sectionBuilderLang.addSave
        }
        cancelLabel={sectionBuilderLang.cancel}
        schema={sectionSchema as ZodType<SectionEditorValues>}
        defaultValues={sectionBuilderDefaults}
        onSubmit={handleSectionSubmit}
        initialOpenSection={'section-config'}
        configCard={
          <Card className={cn('w-full shadow-none')}>
            <CardHeader className={cn('border-b border-neutral-border')}>
              <CardTitle className={'text-lg font-bold'}>
                {'Section configuration'}
              </CardTitle>
            </CardHeader>
            <CardContent className={cn('pt-0')}>
              <FormTextField
                name={'name'}
                label={'Section name'}
                placeholder={'Enter section name'}
              />
            </CardContent>
          </Card>
        }
        fieldOptions={sectionFieldOptions}
        getValueOptions={resolvedGetValueOptions}
        currentEditingId={editingSectionId ?? undefined}
        disallowedCycleCandidates={sectionDisallowedCycleCandidates}
        conditionalLogicDescription={
          'Show this section based on the output from another field'
        }
        conditionalLogicToggleLocked={sectionProtectedFieldIds.length > 0}
        conditionalLogicDisabledReason={
          sectionProtectedFieldIds.length > 0
            ? buildRequiredFieldSectionLockReason(
                sectionProtectedFieldIds,
                fields
              )
            : undefined
        }
        showGuidance={false}
      />
      <SubEditorDialog
        open={open && activeView === 'field-editor'}
        onCancel={returnToFormEditor}
        title={fieldEditorLang.title}
        saveLabel={
          editingFieldId ? fieldEditorLang.editSave : fieldEditorLang.addSave
        }
        cancelLabel={fieldEditorLang.cancel}
        schema={fieldSchema as ZodType<FieldEditorValues>}
        defaultValues={fieldEditorDefaults}
        onSubmit={handleFieldSubmit}
        initialOpenSection={'field-config'}
        configCard={
          <Card className={cn('w-full shadow-none')}>
            <CardHeader className={cn('border-b border-neutral-border')}>
              <CardTitle className={'text-lg font-bold'}>
                {'Field configuration'}
              </CardTitle>
              <CardAction>
                <div className={cn('flex items-center gap-4')}>
                  <ExclusiveSwitchGroup
                    options={[
                      { name: 'required', label: 'Required' },
                      { name: 'readOnly', label: 'Read only' },
                      { name: 'hidden', label: 'Hidden' },
                    ]}
                    disabled={!canEditOptions}
                  />
                </div>
              </CardAction>
            </CardHeader>
            <CardContent className={cn('pt-0')}>
              <FieldEditorConfigFields
                disableFieldType={isFieldTypeDisabled}
                disableFieldName={!canEditLabel}
                fieldTypeOptions={fieldTypeOptions ?? []}
                fieldTypeDisabledReason={
                  !isStandardField && editingFieldIsRuleSource
                    ? "This field's type is locked because it's used as a source in a conditional logic rule. Remove the rule first to change its type."
                    : editingFieldIsPersistedCustom
                      ? "The field type can't be changed once a field has been saved, to protect data already recorded against it. Create a new field to use a different type."
                      : undefined
                }
                originalLabel={editingFieldOriginalLabel}
                isAltValuesEnabled={isAltValuesEnabled}
              />
            </CardContent>
          </Card>
        }
        fieldOptions={fieldOptions}
        getValueOptions={resolvedGetValueOptions}
        currentEditingId={editingFieldId ?? undefined}
        disallowedCycleCandidates={fieldDisallowedCycleCandidates}
        conditionalLogicDescription={
          'Show this field based on the output from another field'
        }
        guidancePlaceholder={'Add guidance text...'}
        showConditionalLogic={editingFieldAllowsTarget}
        conditionalLogicLocked={editingFieldConditionLocked}
        conditionalLogicToggleLocked={editingFieldHasLockedRules}
      />
      <DeleteImpactConfirmationDialog
        impact={pendingDeletion}
        onCancel={() => setPendingDeletion(null)}
        onConfirm={handleConfirmDeletion}
      />
    </>
  );
};
